import { AlertTriangle } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { queryKeys } from "@/api/hooks";
import {
  classifyRunReadFailure,
  highestSequenceOf,
  usePreviewRunEventQuery,
  usePreviewRunEventsQuery,
  type PreviewRunEventsReading,
} from "@/api/previewInsights";
import { QueryPendingState } from "@/api/QueryPendingState";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  EVENT_KIND_LABELS,
  EVENT_STATUS_LABELS,
  EVENT_STATUS_TONES,
  EVIDENCE_FIELDS,
  RATIONALE_STATE_LABELS,
  SAFETY_INTERNALS_FIELD,
  VISIBILITY_LABELS,
  buildDecisionTimeline,
  describeCriterionResult,
  describeDisposition,
  describeGap,
  describeOperator,
  describePosition,
  describeRestrictedSummary,
  describeRoutingReason,
  describeSafetyDetection,
  describeSafetyIntervention,
  describeSafetyMechanism,
  describeSafetyOutcome,
  describeSafetyReason,
  describeWhyNotReason,
  describeWithheld,
  formatScalar,
  positionsDiffer,
  previewRunTabPath,
  readContentSlot,
  readInlineBody,
  resolveEvidence,
  safetyOutcomeLabel,
  safetyOutcomeTone,
  type ClassificationEvent,
  type ContentSlot,
  type CriterionEvent,
  type DecisionTimeline,
  type RoutingEvent,
  type SafetyEvent,
  type TimelineItem,
  type TimelineTurn,
  type TraceEvent,
  type TraceEventCommon,
  type TranscriptEntry,
  type WhyNotEvent,
} from "@/lib/previewDecisions";
import {
  CAPTURE_STATE_LABELS,
  CONTENT_UNAVAILABLE_LABELS,
  OMISSION_REASON_LABELS,
  canStillChange,
  describeView,
  type MissingRange,
  type PreviewRunView,
  type TraceCompleteness,
} from "@/lib/previewInsights";
import { cn } from "@/lib/cn";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { EmptyState, StaleDataNotice } from "@/ui/states";

import { PreviewRunReadFailure } from "./PreviewRunReadFailure";

/**
 * Flow & Decisions — the run page's third tab (#1102, approved design §10.4).
 *
 * A structured timeline of what the runtime decided, read DIRECTLY from the
 * versioned trace events the run's event page lists: classifications, the
 * criteria and thresholds the engine evaluated from them, routing decisions,
 * the alternatives each one did not take, and runtime transitions — grouped
 * by turn, phase and agent, in the order the runtime recorded them.
 *
 * ## What this tab computes, and deliberately does not
 *
 * It computes NOTHING clinical. Every score, count, threshold, verdict, route,
 * disposition and reason on a card is the producer's own value, rendered
 * beside the identity of the configuration that governed it. Where the
 * runtime recorded no explicit rationale the card says so; where a body was
 * withheld the card says why; where a range of events was never captured the
 * timeline holds a gap card in its place. Nothing missing is presented as
 * "false", "no-op" or "unmet", and nothing unmet is presented as missing
 * (ADR 0013 N7, and the design's §16 first risk).
 *
 * ## What it reads, and when
 *
 * The compact event page, and only once the clinician opens this tab: the run
 * page's shell keeps making exactly the reads it made before. Bodies are
 * carried by reference on that page; a classification's recorded result or
 * rationale is read through the one authorized detail route, on the
 * clinician's say-so, and only for an event the page itself listed.
 *
 * ## Evidence links preserve scope and cannot name arbitrary events
 *
 * A decision's evidence references resolve against the loaded events ONLY.
 * A resolved reference becomes a link to the tab that shows its family —
 * a turn opens on Conversation, a prompt call on Prompt Inspector, another
 * decision here — built on the run page's own address, so every link carries
 * the Project id, the opaque run locator, a tab name and an opaque event
 * reference and nothing else. A reference the page did not list is stated
 * as not captured (when the producer declared it lost) or as not loaded,
 * and is never requested to find out.
 *
 * ## Safety outcomes, protected content and restricted internals (#1103)
 *
 * A `safety` event is a card in this same timeline, at its own sequence. It
 * shows what the mechanism was, the state it reported, the reason code bound
 * to that state, whether the reply was evaluated at all, what was done to it,
 * and the governing safety policy's identity — every one of them Brain's own
 * value, none of them derived here. The seven states rule R27 keeps apart are
 * kept apart on screen: `clear` is the only one that reads as a clean result,
 * and a check that failed, was unavailable, was configured off or never ran
 * says so in its own words, with its own chip, and states that it reached no
 * verdict.
 *
 * The event's protected clinical content — the producer's fixed sentence
 * about what this mechanism did to the reply, and the policy identity — is
 * shown READ-ONLY and in full; that is what `protected_clinical` means
 * (design §6.2). The mechanism's internals are `restricted_technical`, and
 * this page shows only the safe summary the design allows: a label, a
 * digest, a size and the outcome. No control on this card edits, approves,
 * overrides or re-runs anything: inspection grants no safety authority.
 *
 * Prompt calls and provenance remain the Prompt Inspector's.
 */
export function PreviewRunDecisions({
  projectId,
  previewRunRef,
  view,
  active,
  paused,
  pollIntervalMs,
  focusEventRef,
}: {
  projectId: string;
  previewRunRef: string;
  view: PreviewRunView;
  /** Whether this tab is the selected one. The read starts the first time it is. */
  active: boolean;
  /** The clinician-held pause from the run status card; affects this view only. */
  paused: boolean;
  pollIntervalMs: number;
  /** The event the address asks this tab to open on, already shape-checked; null for none. */
  focusEventRef: string | null;
}) {
  // THE READ STARTS WHEN THE TAB IS FIRST OPENED and stays enabled after: the
  // panel stays mounted behind the other tabs, so a look at another tab does
  // not throw the list away. Polling follows the run's own state and stops
  // while the tab is not the one on screen.
  const [opened, setOpened] = useState(active);
  if (active && !opened) {
    setOpened(true);
  }
  const readable =
    view.kind === "live" || view.kind === "completed" || view.kind === "incomplete";
  const events = usePreviewRunEventsQuery(projectId, previewRunRef, {
    enabled: readable && opened,
    pollIntervalMs: active && !paused && canStillChange(view) ? pollIntervalMs : false,
  });

  // CATCH UP TO THE ENVELOPE (#1122 review, both rounds). The run envelope
  // and the event page are polled on two timers, and responses can settle in
  // any order: the envelope can observe the run's end — and stop this timer —
  // before the tick that would have read the final events, and a page read
  // that STARTED before the envelope advanced can settle after it, serving
  // less than the envelope now knows. The envelope's `lastSequence` is the
  // highest sequence the run is known to have reached, and a page read sent
  // after the envelope reached a value serves everything present up to it —
  // so the question a settled read answers is "did this read start after the
  // envelope reached what it now says?": if not, one more read follows; if
  // so, what the page did not serve is a declared gap, and re-reading cannot
  // fill it. That bounds the extra reads to one per envelope advance, and
  // reconciles the first read against the envelope it was started under —
  // never against a value it could not have known.
  const envelopeLast = view.summary?.lastSequence ?? null;
  const loadedLast = events.data?.content
    ? (highestSequenceOf(events.data.content.events) ?? 0)
    : null;
  const { refetch, isFetching, isError } = events;
  // The envelope value seen while no read was running — what the next read
  // starts under — and the one the read in flight (or the last read) started
  // under. Written in effects, never during render.
  const envelopeSeenIdle = useRef<number | null>(envelopeLast);
  const envelopeAtStart = useRef<number | null>(null);
  const wasFetching = useRef(false);
  useEffect(() => {
    if (isFetching) {
      if (!wasFetching.current) {
        envelopeAtStart.current = envelopeSeenIdle.current;
      }
    } else {
      envelopeSeenIdle.current = envelopeLast;
    }
    wasFetching.current = isFetching;
  }, [isFetching, envelopeLast]);
  useEffect(() => {
    if (!active || paused || !readable || !opened || isFetching || isError) {
      return;
    }
    if (loadedLast === null || envelopeLast === null || loadedLast >= envelopeLast) {
      return;
    }
    const known = envelopeAtStart.current;
    if (known !== null && known >= envelopeLast) {
      // The last read started after the envelope reached this value and still
      // did not serve it: a declared gap, not a stale read.
      return;
    }
    void refetch();
  }, [active, paused, readable, opened, isFetching, isError, loadedLast, envelopeLast, refetch]);

  if (!readable) {
    return view.kind === "waiting" ? (
      <EmptyState
        title="No decisions recorded yet"
        description={`${describeView(view)} Decision cards appear here once the runtime records a turn.`}
      />
    ) : (
      <EmptyState
        title="No decisions can be shown"
        description="The trace for this run is unavailable, so its decisions cannot be read. The run status above says why; nothing is inferred in their place."
      />
    );
  }

  if (events.isPending || !events.isFetchedAfterMount) {
    return (
      <QueryPendingState
        isPaused={events.isPaused}
        failureReason={events.failureReason}
        label="Loading decisions"
        errorTitle="This preview run could not be read"
        rows={4}
        queryKey={queryKeys.previewRunEvents(projectId, previewRunRef)}
      />
    );
  }

  if (events.isError && (!events.data || isTerminalQueryError(events.error))) {
    return (
      <PreviewRunReadFailure
        failure={classifyRunReadFailure(events.error)}
        error={events.error}
        onRetry={() => void events.refetch()}
      />
    );
  }

  return (
    <DecisionTimelineView
      projectId={projectId}
      previewRunRef={previewRunRef}
      reading={events.data}
      completeness={view.summary?.completeness ?? null}
      stale={events.isError}
      staleAt={events.dataUpdatedAt}
      onRetry={() => void events.refetch()}
      active={active}
      focusEventRef={focusEventRef}
    />
  );
}

function DecisionTimelineView({
  projectId,
  previewRunRef,
  reading,
  completeness,
  stale,
  staleAt,
  onRetry,
  active,
  focusEventRef,
}: {
  projectId: string;
  previewRunRef: string;
  reading: PreviewRunEventsReading;
  completeness: TraceCompleteness | null;
  stale: boolean;
  staleAt: number;
  onRetry: () => void;
  active: boolean;
  focusEventRef: string | null;
}) {
  const container = useRef<HTMLDivElement>(null);
  const content = reading.content;
  const timeline = content ? buildDecisionTimeline(content.events, completeness) : null;
  const loaded = content?.events.length ?? 0;
  const focusTarget =
    focusEventRef !== null && timeline !== null && timeline.index.has(focusEventRef)
      ? focusEventRef
      : null;

  // THE ADDRESS OPENS ONE CARD. When the link that brought the clinician here
  // named an event this run lists, focus lands on its card — the way the run
  // page focuses its heading after an in-app navigation — and the card scrolls
  // into view. A reference the run does not list moves focus nowhere and is
  // stated below. The selector is safe by construction: `focusEventRef` has
  // already been held to the producer's `evt_` + hex shape.
  useEffect(() => {
    if (!active || focusTarget === null) {
      return;
    }
    const target = container.current?.querySelector<HTMLElement>(
      `[data-event-ref="${focusTarget}"]`,
    );
    if (target) {
      target.focus();
      if (typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: "center" });
      }
    }
  }, [active, focusTarget, loaded]);

  if (content === null || timeline === null) {
    const reason = reading.run.contentUnavailableReason ?? null;
    return (
      <EmptyState
        title="No decisions can be shown"
        description={`The recorded preview target reports no readable content for this run right now${
          reason ? `: ${CONTENT_UNAVAILABLE_LABELS[reason]}` : ""
        } (capture: ${CAPTURE_STATE_LABELS[reading.run.captureState].toLowerCase()}). Nothing is inferred in its place.`}
      />
    );
  }

  if (content.events.length === 0) {
    return (
      <EmptyState
        title="No events recorded yet"
        description="The trace holds no events yet. Decision cards appear here once the runtime records a turn."
      />
    );
  }

  const turnCount = timeline.turns.filter((turn) => turn.turnNumber !== null).length;

  return (
    <div ref={container} className="space-y-6">
      <StaleDataNotice updatedAt={staleAt} onRetry={onRetry} visible={stale} />

      {!content.complete ? (
        <Notice icon={<AlertTriangle aria-hidden="true" className="h-4 w-4" />}>
          {`Only the first ${loaded} event${loaded === 1 ? "" : "s"} ${loaded === 1 ? "is" : "are"} shown: the run kept reporting more after its cursor stopped advancing, or after more events than the producer contract allows had been served. An operator should check the run's event page.`}
        </Notice>
      ) : null}

      {focusEventRef !== null && focusTarget === null ? (
        <p
          role="status"
          className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-foreground"
        >
          The event named in this link is not among this run's recorded events.
          Nothing was requested for it.
        </p>
      ) : null}

      <p className="text-sm text-muted-foreground">
        {describeCounts(
          timeline.decisionCount,
          timeline.safetyCount,
          turnCount,
          timeline.otherCount,
        )}
      </p>

      {timeline.decisionCount === 0 &&
      timeline.safetyCount === 0 &&
      timeline.unplaceable.length === 0 &&
      timeline.leadingGaps.length === 0 &&
      !hasAnyGap(timeline) ? (
        <EmptyState
          title="No decision or safety events in this run"
          description="None of the recorded events is a classification, criterion, routing decision, alternative, runtime transition or safety outcome. The prompt calls this run recorded are shown on the Prompt Inspector; nothing is inferred from them here."
        />
      ) : null}

      {timeline.leadingGaps.map((range, index) => (
        <GapCard key={`leading-${index}`} range={range} />
      ))}

      <ol className="space-y-6">
        {timeline.turns.map((turn) => (
          <TurnSection
            key={turn.turnNumber ?? `before-${turn.firstSequence}`}
            turn={turn}
            timeline={timeline}
            completeness={completeness}
            projectId={projectId}
            previewRunRef={previewRunRef}
          />
        ))}
      </ol>

      {timeline.unplaceable.length > 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          {`${timeline.unplaceable.length} record${
            timeline.unplaceable.length === 1 ? "" : "s"
          } on this run's event page could not be read by this build and could not be placed in the timeline.`}
        </p>
      ) : null}
    </div>
  );
}

/** Whether any declared missing range is placed anywhere in the turns. */
function hasAnyGap(timeline: DecisionTimeline): boolean {
  return timeline.turns.some(
    (turn) =>
      turn.leadingGaps.length > 0 ||
      turn.phases.some((phase) =>
        phase.agents.some((agent) => agent.items.some((item) => item.kind === "gap")),
      ),
  );
}

/**
 * The tab's one-line summary — counts only, never a clinical statement.
 *
 * ONE STRING, deliberately: a sentence assembled from `<span dir="ltr">`
 * digits is unmatchable by an assistive reader's own text and by a test's
 * (#1122), so the numbers are formatted into the sentence itself.
 */
function describeCounts(
  decisions: number,
  safety: number,
  turns: number,
  others: number,
): string {
  const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;
  const lead = `${plural(decisions, "decision event")} and ${plural(
    safety,
    "safety outcome",
  )} across ${plural(turns, "turn")}`;
  return others > 0
    ? `${lead} · ${plural(others, "prompt event")} shown on the Prompt Inspector.`
    : `${lead}.`;
}

// ── Groups ───────────────────────────────────────────────────────────────────

function TurnSection({
  turn,
  timeline,
  completeness,
  projectId,
  previewRunRef,
}: {
  turn: TimelineTurn;
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  const headingId = useId();
  const cards = turn.phases.flatMap((phase) => phase.agents.flatMap((agent) => agent.items));
  return (
    <li>
      <section aria-labelledby={headingId} className="space-y-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 id={headingId} className="text-base font-semibold text-foreground">
            {turn.turnNumber !== null ? (
              <>
                Turn <span dir="ltr">{turn.turnNumber}</span>
              </>
            ) : (
              "Before the first recorded turn"
            )}
          </h3>
          {turn.turn ? (
            <TurnPosition event={turn.turn} />
          ) : turn.turnNumber !== null ? (
            <span className="text-xs text-muted-foreground">
              No turn event is recorded for this turn; its exchange is not shown.
            </span>
          ) : null}
        </div>

        {cards.length === 0 && turn.leadingGaps.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No decision or safety events were recorded in this turn.
          </p>
        ) : null}

        {turn.leadingGaps.map((range, index) => (
          <GapCard key={`leading-gap-${index}`} range={range} />
        ))}

        {turn.phases.map((phase) =>
          phase.agents.map((agent) => (
            <AgentGroup
              key={`${phase.phase ?? "-"}::${agent.agent ?? "-"}`}
              phase={phase.phase}
              agent={agent.agent}
              items={agent.items}
              timeline={timeline}
              completeness={completeness}
              projectId={projectId}
              previewRunRef={previewRunRef}
            />
          )),
        )}
      </section>
    </li>
  );
}

/** The turn's own position before and after, as the turn event recorded it. */
function TurnPosition({ event }: { event: Extract<TraceEvent, { family: "turn" }> }) {
  const { before, after } = event.detail;
  return (
    <span className="text-xs text-muted-foreground">
      {positionsDiffer(before, after) ? (
        <>
          From {describePosition(before)} to {describePosition(after)}
        </>
      ) : (
        <>At {describePosition(after)}</>
      )}
    </span>
  );
}

function AgentGroup({
  phase,
  agent,
  items,
  timeline,
  completeness,
  projectId,
  previewRunRef,
}: {
  phase: string | null;
  agent: string | null;
  items: readonly TimelineItem[];
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h4
        id={headingId}
        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        Phase{" "}
        {phase !== null ? <code dir="ltr" className="break-all font-mono">{phase}</code> : "not recorded"}
        {" · "}Agent{" "}
        {agent !== null ? <code dir="ltr" className="break-all font-mono">{agent}</code> : "not recorded"}
      </h4>
      <ol className="space-y-3">
        {items.map((item, index) => (
          <li key={itemKey(item, index)}>
            <ItemCard
              item={item}
              timeline={timeline}
              completeness={completeness}
              projectId={projectId}
              previewRunRef={previewRunRef}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}

function itemKey(item: TimelineItem, index: number): string {
  switch (item.kind) {
    case "gap":
      return `gap-${item.sequence}`;
    case "unreadable":
      return `unreadable-${item.reading.eventRef ?? index}`;
    default:
      return item.event.eventRef;
  }
}

function ItemCard(props: {
  item: TimelineItem;
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  const { item } = props;
  switch (item.kind) {
    case "gap":
      return <GapCard range={item.range} />;
    case "classification":
      return (
        <ClassificationCard
          {...props}
          event={item.event}
          transcript={item.transcript}
          verdict={item.verdict}
        />
      );
    case "criterion":
      return <CriterionCard event={item.event} />;
    case "routing":
      return <RoutingCard {...props} event={item.event} transcript={item.transcript} />;
    case "why_not":
      return <OrphanAlternativeCard {...props} event={item.event} />;
    case "transition":
      return <TransitionCard event={item.event} />;
    case "safety":
      return <SafetyCard event={item.event} completeness={props.completeness} />;
    case "unreadable_detail":
      return (
        <EventCard event={item.event} eyebrow={EVENT_KIND_LABELS[item.event.kind]}>
          <p className="text-sm text-muted-foreground">
            This build could not read this event's detail (
            <code dir="ltr" className="font-mono text-xs">{item.event.reason}</code>
            ). The event is shown by its own label, status and position only;
            nothing is inferred about its content.
          </p>
        </EventCard>
      );
    case "unreadable":
      return (
        <article
          className="rounded-md border border-dashed border-border bg-card px-4 py-3 text-sm text-muted-foreground"
          {...(item.reading.eventRef !== null
            ? { "data-event-ref": item.reading.eventRef, tabIndex: -1 }
            : {})}
        >
          A record on this run's event page could not be read by this build (
          <code dir="ltr" className="font-mono text-xs">{item.reading.reason}</code>
          {item.reading.eventKind !== null ? (
            <>
              , reported kind <code dir="ltr" className="font-mono text-xs">{item.reading.eventKind}</code>
            </>
          ) : null}
          ). It is placed at event{" "}
          <span dir="ltr">{item.reading.sequence ?? "?"}</span> and nothing is inferred about it.
        </article>
      );
  }
}

// ── Cards ────────────────────────────────────────────────────────────────────

/**
 * The frame every card shares: the family, the producer's own label, the
 * chips a clinician must not miss (a status other than `ok`, a visibility
 * other than full clinical content), and the sequence. The `<article>` is a
 * focus target for the address and for in-tab evidence links.
 */
function EventCard({
  event,
  eyebrow,
  tone = "default",
  children,
}: {
  event: TraceEventCommon;
  eyebrow: string;
  /** `attention` draws the warning border a state a clinician must not skim needs. */
  tone?: "default" | "attention";
  children: ReactNode;
}) {
  const headingId = useId();
  return (
    <article
      aria-labelledby={headingId}
      data-event-ref={event.eventRef}
      tabIndex={-1}
      className={cn(
        "rounded-md border bg-card px-4 py-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        tone === "attention" ? "border-warning/60" : "border-border",
      )}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {eyebrow}
          </p>
          <h5 id={headingId} dir="auto" className="text-sm font-semibold text-foreground">
            {event.label}
          </h5>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {event.status !== "ok" ? (
            <Badge tone={EVENT_STATUS_TONES[event.status]}>
              {EVENT_STATUS_LABELS[event.status]}
            </Badge>
          ) : null}
          {event.visibility !== "clinical_full" ? (
            <Badge tone="outline">{VISIBILITY_LABELS[event.visibility]}</Badge>
          ) : null}
          <span className="text-xs text-muted-foreground">
            Event <span dir="ltr">{event.sequence}</span>
          </span>
        </div>
      </header>
      <div className="mt-3 space-y-3">{children}</div>
    </article>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 break-words text-foreground">{children}</dd>
    </div>
  );
}

function Identifier({ value }: { value: string }) {
  return <code dir="ltr" className="break-all font-mono text-xs">{value}</code>;
}

function ClassificationCard({
  event,
  transcript,
  verdict,
  timeline,
  completeness,
  projectId,
  previewRunRef,
}: {
  event: ClassificationEvent;
  /** The criteria in the engine's order, with any declared gap between them. */
  transcript: readonly TranscriptEntry<CriterionEvent>[];
  verdict: CriterionEvent | null;
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  const { detail } = event;
  const resultWithheld = describeWithheld(detail.result);
  // A recorded rationale the producer WITHHELD (size cap, policy) is stated
  // from the compact event itself — there is no reference to open, so no
  // control offers to and no detail read is made (#1122 review).
  const rationaleWithheld = detail.rationale ? describeWithheld(detail.rationale) : null;
  return (
    <EventCard
      event={event}
      eyebrow={EVENT_KIND_LABELS.classification}
      tone={event.visibility === "restricted_technical" ? "attention" : "default"}
    >
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Fact label="Evaluated">
          Classifier <Identifier value={detail.classifierId} />
        </Fact>
        <Fact label="Evidence">
          <EvidenceList
            refs={detail.inputRefs}
            citing={{ eventRef: event.eventRef, field: EVIDENCE_FIELDS.classification }}
            timeline={timeline}
            completeness={completeness}
            projectId={projectId}
            previewRunRef={previewRunRef}
          />
        </Fact>
        <Fact label="Result">
          {verdict ? (
            <span className="flex flex-wrap items-center gap-2">
              <span dir="auto">{verdict.label}</span>
              <Badge tone={verdict.detail.passed ? "success" : "neutral"}>
                {describeCriterionResult(verdict.detail.passed)}
              </Badge>
            </span>
          ) : event.status === "no_action" ? (
            "Not applied: the runtime classified and then set this result aside."
          ) : (
            "No verdict criterion was recorded for this classification."
          )}
        </Fact>
        <Fact label="Score">
          {detail.score ? (
            <>
              <span dir="ltr">{detail.score.value}</span> ({detail.score.scale})
            </>
          ) : (
            "The runtime owns no score for this classification; none is derived here."
          )}
        </Fact>
        <Fact label="Rationale">
          {RATIONALE_STATE_LABELS[detail.rationaleState]}
          {detail.rationaleState === "recorded" && detail.rationale ? (
            rationaleWithheld !== null ? (
              <span className="mt-1 block text-muted-foreground">{rationaleWithheld}</span>
            ) : (
            <RecordedBody
              projectId={projectId}
              previewRunRef={previewRunRef}
              eventRef={event.eventRef}
              listed={timeline.index.has(event.eventRef)}
              slotName="rationale"
              slot={detail.rationale}
              openLabel="Show recorded rationale"
              closeLabel="Hide recorded rationale"
            />
            )
          ) : null}
        </Fact>
        <Fact label="Recorded result">
          {resultWithheld ?? (
            <RecordedBody
              projectId={projectId}
              previewRunRef={previewRunRef}
              eventRef={event.eventRef}
              listed={timeline.index.has(event.eventRef)}
              slotName="result"
              slot={detail.result}
              openLabel="Show recorded result"
              closeLabel="Hide recorded result"
            />
          )}
        </Fact>
      </dl>

      {transcript.length > 0 ? (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Criteria evaluated, in the order the engine evaluated them
          </p>
          <ol className="mt-1 divide-y divide-border">
            {transcript.map((entry) =>
              entry.kind === "gap" ? (
                <li key={`gap-${entry.sequence}`} className="py-2">
                  <GapRow range={entry.range} />
                </li>
              ) : (
                <li
                  key={entry.event.eventRef}
                  data-event-ref={entry.event.eventRef}
                  tabIndex={-1}
                  className="py-2 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <CriterionRow event={entry.event} />
                </li>
              ),
            )}
          </ol>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No criterion events were recorded for this classification.
        </p>
      )}
    </EventCard>
  );
}

/** One criterion: what was observed, what was required, and the engine's own verdict. */
function CriterionRow({ event }: { event: CriterionEvent }) {
  const { detail } = event;
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0 space-y-0.5">
        <p dir="auto" className="font-medium text-foreground">
          {event.label}
        </p>
        <p className="text-muted-foreground">
          {`Observed ${formatScalar(detail.observedValue)}; required ${describeOperator(detail.operator)} ${formatScalar(detail.threshold)}.`}
        </p>
        <p className="text-xs text-muted-foreground">
          Criterion <Identifier value={detail.criterionId} /> · source{" "}
          <Identifier value={detail.sourceVersion} />
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone={detail.passed ? "success" : "neutral"}>
          {describeCriterionResult(detail.passed)}
        </Badge>
        {event.status !== "ok" ? (
          <Badge tone={EVENT_STATUS_TONES[event.status]}>
            {EVENT_STATUS_LABELS[event.status]}
          </Badge>
        ) : null}
        {event.visibility !== "clinical_full" ? (
          <Badge tone="outline">{VISIBILITY_LABELS[event.visibility]}</Badge>
        ) : null}
      </div>
    </div>
  );
}

function CriterionCard({ event }: { event: CriterionEvent }) {
  const { detail } = event;
  return (
    <EventCard event={event} eyebrow={EVENT_KIND_LABELS.criterion_threshold}>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Fact label="Criterion">
          <Identifier value={detail.criterionId} />
        </Fact>
        <Fact label="Result">
          <Badge tone={detail.passed ? "success" : "neutral"}>
            {describeCriterionResult(detail.passed)}
          </Badge>
        </Fact>
        <Fact label="Observed">
          <span dir="ltr">{formatScalar(detail.observedValue)}</span>
        </Fact>
        <Fact label="Required">
          {describeOperator(detail.operator)}{" "}
          <span dir="ltr">{formatScalar(detail.threshold)}</span>
        </Fact>
        <Fact label="Source version">
          <Identifier value={detail.sourceVersion} />
        </Fact>
      </dl>
      <p className="text-xs text-muted-foreground">
        No classification event in this group owns this criterion; it is shown on its own.
      </p>
    </EventCard>
  );
}

function RoutingCard({
  event,
  transcript,
  timeline,
  completeness,
  projectId,
  previewRunRef,
}: {
  event: RoutingEvent;
  /** The alternatives in the producer's order, with any declared gap between them. */
  transcript: readonly TranscriptEntry<WhyNotEvent>[];
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  const { detail } = event;
  return (
    <EventCard event={event} eyebrow={EVENT_KIND_LABELS.routing}>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Fact label="Decision">
          <Identifier value={detail.decisionId} />
        </Fact>
        <Fact label="Selected route">
          <strong dir="auto">{detail.selectedChoice}</strong>
        </Fact>
        <Fact label="Rule that decided">
          {describeRoutingReason(detail.reasonCode)}{" "}
          <Identifier value={detail.reasonCode} />
        </Fact>
        <Fact label="Governing configuration">
          <ConfigIdentityText config={detail.governingConfig} />
        </Fact>
        <Fact label="Considered, in the order the rule ranged over them">
          <ol className="list-decimal ps-5">
            {detail.consideredChoices.map((choice) => (
              <li key={choice} dir="auto">
                {choice}
                {choice === detail.selectedChoice ? (
                  <span className="text-muted-foreground"> (selected)</span>
                ) : null}
              </li>
            ))}
          </ol>
        </Fact>
        <Fact label="Evidence">
          <EvidenceList
            refs={detail.evidenceRefs}
            citing={{ eventRef: event.eventRef, field: EVIDENCE_FIELDS.routing }}
            timeline={timeline}
            completeness={completeness}
            projectId={projectId}
            previewRunRef={previewRunRef}
          />
        </Fact>
      </dl>

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Alternatives not selected
        </p>
        {transcript.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">
            No alternative was recorded for this decision.
          </p>
        ) : (
          <ol className="mt-1 divide-y divide-border">
            {transcript.map((entry) =>
              entry.kind === "gap" ? (
                <li key={`gap-${entry.sequence}`} className="py-2">
                  <GapRow range={entry.range} />
                </li>
              ) : (
                <li
                  key={entry.event.eventRef}
                  data-event-ref={entry.event.eventRef}
                  tabIndex={-1}
                  className="py-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <AlternativeBody
                    event={entry.event}
                    timeline={timeline}
                    completeness={completeness}
                    projectId={projectId}
                    previewRunRef={previewRunRef}
                  />
                </li>
              ),
            )}
          </ol>
        )}
      </div>
    </EventCard>
  );
}

/**
 * One alternative, exactly as the runtime stood it: its disposition (the
 * chip), the rule's reason, the score the selector compared — or the fact
 * that it held none — the unmet criteria it named, and the configuration
 * that cost THIS alternative its place.
 */
function AlternativeBody({
  event,
  timeline,
  completeness,
  projectId,
  previewRunRef,
}: {
  event: WhyNotEvent;
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  const { detail } = event;
  const unavailable = detail.disposition === "unavailable";
  return (
    <div className="space-y-1 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <strong dir="auto">{detail.alternative}</strong>
        <Badge tone={unavailable ? "destructive" : "outline"}>
          {describeDisposition(detail.disposition)}
        </Badge>
        {event.status !== "ok" && event.status !== "no_action" ? (
          <Badge tone={EVENT_STATUS_TONES[event.status]}>
            {EVENT_STATUS_LABELS[event.status]}
          </Badge>
        ) : null}
      </div>
      <p dir="auto" className="text-muted-foreground">
        {event.label}
      </p>
      <p>
        {describeWhyNotReason(detail.reasonCode)} <Identifier value={detail.reasonCode} />
      </p>
      <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        <Fact label="Compared score">
          {detail.comparedScore ? (
            <>
              <span dir="ltr">{detail.comparedScore.value}</span> ({detail.comparedScore.scale})
            </>
          ) : (
            "No score was held for it — not a score of zero."
          )}
        </Fact>
        <Fact label="Unmet criteria">
          {unavailable ? (
            "Not evaluated: the classification produced no answer, so no criterion was met or unmet."
          ) : detail.unmetCriteria.length === 0 ? (
            "None named by the runtime."
          ) : (
            <ul className="flex flex-wrap gap-x-2 gap-y-1">
              {detail.unmetCriteria.map((criterion) => (
                <li key={criterion}>
                  <Identifier value={criterion} />
                </li>
              ))}
            </ul>
          )}
        </Fact>
        <Fact label="Governing configuration">
          <ConfigIdentityText config={detail.governingConfig} />
        </Fact>
        <Fact label="Evidence">
          <EvidenceList
            refs={detail.evidenceRefs}
            citing={{ eventRef: event.eventRef, field: EVIDENCE_FIELDS.why_not }}
            timeline={timeline}
            completeness={completeness}
            projectId={projectId}
            previewRunRef={previewRunRef}
          />
        </Fact>
      </dl>
    </div>
  );
}

function OrphanAlternativeCard(props: {
  event: WhyNotEvent;
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  return (
    <EventCard event={props.event} eyebrow={EVENT_KIND_LABELS.why_not}>
      <p className="text-sm text-muted-foreground">
        The routing decision this alternative refers to is not among the loaded events,
        so it is shown on its own rather than beside a decision it did not name.
      </p>
      <AlternativeBody {...props} />
    </EventCard>
  );
}

/**
 * The safe summary of a mechanism's restricted internals — a label, a digest,
 * a size, and nothing that can be opened (design §6.2).
 *
 * The digest is the producer's own, over the record it held and withheld; it
 * is not content and cannot be turned back into any, and `readContentSlot`
 * has already held it to `sha256:<64 hex>` so it cannot be a sentence either.
 *
 * THE REFUSAL BRANCH is the one that matters: a record classified restricted
 * that arrived carrying a body, a reference, another state or reason, or
 * metadata that is not the contract's was refused whole at the reader, and
 * this says so — loudly, as a fault of the record, rather than quietly as one
 * more withheld body. **It renders NOTHING ELSE from that record.** A digest
 * and a size are a description of a withholding; repeating them beside a
 * refusal would assert that the description is sound, which is the one thing
 * the refusal denies.
 */
function RestrictedInternals({
  slot,
  declared,
}: {
  slot: ContentSlot;
  /** Whether the envelope's completeness index declares this withholding (R18). */
  declared: boolean;
}) {
  if (slot.restrictedContentRefused) {
    return (
      <span role="alert" className="block text-destructive">
        {describeWithheld(slot)}
      </span>
    );
  }
  return (
    <>
      <span className="block">{describeRestrictedSummary(slot)}</span>
      {slot.contentHash !== null ? (
        <span className="mt-0.5 block">
          Digest <Identifier value={slot.contentHash} />
        </span>
      ) : null}
      {declared ? null : (
        <span className="mt-0.5 block text-muted-foreground">
          This run's completeness index does not declare the withholding for this event.
        </span>
      )}
    </>
  );
}

/**
 * One safety mechanism's report on one reply surface (#1103, design §6.2 and
 * §7.3; producer rule R27).
 *
 * EVERY VALUE IS BRAIN'S. The state, the reason code bound to it, whether the
 * reply was evaluated, what was done to it and the governing policy identity
 * are read from the event and rendered; the sentence about what this check
 * did to the reply is the producer's own fixed literal, shown verbatim. This
 * card computes no safety verdict, re-reads none, and softens none.
 *
 * THE STATES STAY APART. `clear` is the only state that wears a success chip
 * and the only one whose card is not bordered for attention. A check that
 * failed or was unavailable wears a destructive chip, a check configured off
 * or one that detected or intervened wears a warning one, and a check that
 * never ran wears an outline one — each with its own words, so no two of the
 * seven read alike. A state this build does not know is said to be unknown
 * and is never resolved to the clean one.
 *
 * PROTECTED, READ-ONLY, AND NOT AN AUTHORITY. The protected clinical content
 * is shown in full and read-only; the mechanism's internals are restricted,
 * withheld at source, and summarised by digest and size alone. Nothing on the
 * card edits, approves, overrides or re-runs a safety control.
 */
function SafetyCard({
  event,
  completeness,
}: {
  event: SafetyEvent;
  completeness: TraceCompleteness | null;
}) {
  const { detail } = event;
  const mechanismName = describeSafetyMechanism(detail.mechanism);
  const internals = detail.guardInternals;
  // The envelope declares the withheld internals as an omission (R18). Saying
  // so distinguishes "withheld on purpose, and the producer said so" from
  // "this slot happens to be empty" — the same distinction every other card
  // on this tab draws between a policy decision and a shortfall.
  const declared =
    completeness?.omissions.some(
      (omission) =>
        omission.eventRef === event.eventRef && omission.field === SAFETY_INTERNALS_FIELD,
    ) ?? false;
  return (
    <EventCard
      event={event}
      eyebrow={EVENT_KIND_LABELS.safety}
      tone={detail.outcome === "clear" ? "default" : "attention"}
    >
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={safetyOutcomeTone(detail.outcome)}>{safetyOutcomeLabel(detail.outcome)}</Badge>
        <span className="text-foreground">{describeSafetyOutcome(detail.outcome)}</span>
      </p>

      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Fact label="Safety check">
          {mechanismName !== null ? (
            <>
              {mechanismName} <Identifier value={detail.mechanism} />
            </>
          ) : (
            <>
              <Identifier value={detail.mechanism} /> — a mechanism this build has no name for.
            </>
          )}
        </Fact>
        <Fact label="Why this state">
          {describeSafetyReason(detail.reasonCode)} <Identifier value={detail.reasonCode} />
        </Fact>
        <Fact label="Was the reply evaluated">
          {describeSafetyDetection(detail.detection)}
        </Fact>
        <Fact label="What this check did to the reply">
          {describeSafetyIntervention(detail.intervention)}
        </Fact>
        <Fact label="Effect on the reply, as the runtime recorded it">
          <span dir="auto">{detail.clinicalEffect}</span>
        </Fact>
        <Fact label="Governing safety policy">
          <ConfigIdentityText config={detail.policyIdentity} />
        </Fact>
        <Fact label="Technical internals">
          <RestrictedInternals slot={internals} declared={declared} />
        </Fact>
      </dl>

      <p className="text-xs text-muted-foreground">
        Recorded safety evidence, shown read-only. Inspecting a preview run grants no
        authority over safety: nothing here edits, approves, overrides or re-runs a
        safety control.
      </p>
    </EventCard>
  );
}

function TransitionCard({ event }: { event: Extract<TraceEvent, { family: "runtime_transition" }> }) {
  const { detail } = event;
  return (
    <EventCard event={event} eyebrow={EVENT_KIND_LABELS.runtime_transition}>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Fact label="From">{describePosition(detail.from)}</Fact>
        <Fact label="To">{describePosition(detail.to)}</Fact>
        <Fact label="Reason code">
          {detail.reasonCode !== null ? (
            <Identifier value={detail.reasonCode} />
          ) : (
            "Not reported"
          )}
        </Fact>
      </dl>
    </EventCard>
  );
}

/** A declared missing range inside a card's transcript — the same statement, as a row. */
function GapRow({ range }: { range: MissingRange }) {
  return (
    <div className="rounded-md border border-dashed border-warning/60 bg-warning/10 px-3 py-2 text-sm text-foreground">
      <span className="me-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Not captured
      </span>
      {describeGap(range)}
    </div>
  );
}

function GapCard({ range }: { range: MissingRange }) {
  return (
    <article className="rounded-md border border-dashed border-warning/60 bg-warning/10 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Not captured
      </p>
      <p className="mt-1 text-sm text-foreground">{describeGap(range)}</p>
    </article>
  );
}

function ConfigIdentityText({
  config,
}: {
  config: { id: string; version: string } | null;
}) {
  if (config === null) {
    return <>Not reported</>;
  }
  return (
    <>
      <Identifier value={config.id} /> at <Identifier value={config.version} />
    </>
  );
}

// ── Evidence ─────────────────────────────────────────────────────────────────

function EvidenceList({
  refs,
  citing,
  timeline,
  completeness,
  projectId,
  previewRunRef,
}: {
  refs: readonly string[];
  citing: { eventRef: string; field: string };
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  if (refs.length === 0) {
    return <>No evidence references were recorded.</>;
  }
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1">
      {refs.map((ref, index) => (
        <li key={ref}>
          <EvidenceLink
            reference={ref}
            citing={{ ...citing, index }}
            timeline={timeline}
            completeness={completeness}
            projectId={projectId}
            previewRunRef={previewRunRef}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * One evidence reference: a link when the run's own event page listed it
 * (to the tab that shows its family), a stated loss when the producer
 * declared it lost, and a stated absence otherwise. Never a request.
 */
function EvidenceLink({
  reference,
  citing,
  timeline,
  completeness,
  projectId,
  previewRunRef,
}: {
  reference: string;
  citing: { eventRef: string; field: string; index: number };
  timeline: DecisionTimeline;
  completeness: TraceCompleteness | null;
  projectId: string;
  previewRunRef: string;
}) {
  const resolution = resolveEvidence(reference, citing, timeline.index, completeness);
  switch (resolution.kind) {
    case "resolved": {
      const { event, destination } = resolution;
      const text = (
        <>
          <span dir="auto">{event.label}</span> (event <span dir="ltr">{event.sequence}</span>)
        </>
      );
      const suffix =
        destination === "conversation"
          ? " — Conversation"
          : destination === "prompts"
            ? " — Prompt Inspector"
            : "";
      return (
        <Link
          to={previewRunTabPath(projectId, previewRunRef, destination, event.eventRef)}
          className="underline underline-offset-2 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {text}
          {suffix}
        </Link>
      );
    }
    case "declared_lost":
      return (
        <span className="text-muted-foreground">
          Evidence not captured
          {resolution.reason ? `: ${OMISSION_REASON_LABELS[resolution.reason]}` : ""}
        </span>
      );
    case "unresolved":
      return (
        <span className="text-muted-foreground">
          Evidence not among this run's loaded events
        </span>
      );
  }
}

// ── Bodies, read on the clinician's say-so ───────────────────────────────────

/**
 * A body the compact page carries by reference, opened through the one
 * authorized detail read — and only for an event the page itself listed
 * (`listed`), which is what keeps an address from making the browser ask
 * about an arbitrary event. Rendered as text or as a structured list, never
 * as markup.
 */
function RecordedBody({
  projectId,
  previewRunRef,
  eventRef,
  listed,
  slotName,
  slot,
  openLabel,
  closeLabel,
}: {
  projectId: string;
  previewRunRef: string;
  eventRef: string;
  listed: boolean;
  slotName: "result" | "rationale";
  slot: ContentSlot;
  openLabel: string;
  closeLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const regionId = useId();
  const detail = usePreviewRunEventQuery(projectId, previewRunRef, eventRef, {
    enabled: open && listed,
  });

  if (slot.state === "inline") {
    return <BodyText slot={slot} />;
  }
  const withheld = describeWithheld(slot);
  if (withheld !== null) {
    // Nothing to open: the compact event already says why (R8/R11 — a
    // withheld body carries no reference), so no control and no read.
    return <>{withheld}</>;
  }
  if (!listed) {
    return <>Not opened: this event is not among the run's listed events.</>;
  }

  return (
    <div className="mt-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? closeLabel : openLabel}
      </Button>
      {open ? (
        <div id={regionId} className="mt-2">
          {detail.isPending ? (
            <p className="text-sm text-muted-foreground">Reading the recorded body…</p>
          ) : detail.isError ? (
            <p role="alert" className="text-sm text-destructive">
              The recorded body could not be read
              {describeDetailFailure(detail.error)}.{" "}
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => void detail.refetch()}
              >
                Try again
              </button>
            </p>
          ) : (
            <DetailSlot event={detail.data.event} slotName={slotName} />
          )}
        </div>
      ) : null}
    </div>
  );
}

function describeDetailFailure(error: unknown): string {
  const failure = classifyRunReadFailure(error);
  switch (failure.kind) {
    case "not_found":
      return ": the run no longer answers for this event";
    case "denied":
      return ": your account is not authorized to read it";
    case "upstream":
      return failure.recovery === "retry"
        ? ": the preview target took too long to answer"
        : ": the preview target could not answer";
    case "error":
      return ` (${failure.code})`;
    case "transport":
      return ": the Editor Service could not be reached";
    case "unauthenticated":
    case "not_implemented":
      return "";
  }
}

function DetailSlot({
  event,
  slotName,
}: {
  event: Record<string, unknown> | null | undefined;
  slotName: "result" | "rationale";
}) {
  const detail = event?.["detail"];
  const slot =
    typeof detail === "object" && detail !== null && !Array.isArray(detail)
      ? readContentSlot((detail as Record<string, unknown>)[slotName])
      : null;
  if (slot === null) {
    return (
      <p className="text-sm text-muted-foreground">
        The detail read carried no readable body for this field.
      </p>
    );
  }
  const withheld = describeWithheld(slot);
  if (withheld !== null) {
    return <p className="text-sm text-muted-foreground">{withheld}</p>;
  }
  return <BodyText slot={slot} />;
}

function BodyText({ slot }: { slot: ContentSlot }) {
  const body = readInlineBody(slot);
  if (body === null) {
    return (
      <p className="text-sm text-muted-foreground">
        The body is carried by reference and was not read.
      </p>
    );
  }
  if (body.kind === "text") {
    return (
      <pre
        dir="auto"
        className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3 font-sans text-sm text-foreground"
      >
        {body.text}
      </pre>
    );
  }
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
      <StructuredValue value={body.value} depth={0} />
    </div>
  );
}

const MAX_STRUCTURED_DEPTH = 8;

/**
 * A structured clinical output — the classifier's own answers — as nested
 * lists of text. Booleans read as yes/no, everything else as the value's own
 * text; keys are identifiers and keep their own direction. Markup in a value
 * is text here, by construction.
 */
function StructuredValue({ value, depth }: { value: unknown; depth: number }) {
  if (depth > MAX_STRUCTURED_DEPTH) {
    return <span className="text-muted-foreground">nested deeper than shown</span>;
  }
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">not set</span>;
  }
  if (typeof value === "boolean") {
    return <span>{value ? "Yes" : "No"}</span>;
  }
  if (typeof value === "number") {
    return <span dir="ltr">{String(value)}</span>;
  }
  if (typeof value === "string") {
    return (
      <span dir="auto" className="whitespace-pre-wrap break-words">
        {value}
      </span>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-muted-foreground">empty list</span>;
    }
    return (
      <ol className="list-decimal ps-5">
        {value.map((item, index) => (
          <li key={index}>
            <StructuredValue value={item} depth={depth + 1} />
          </li>
        ))}
      </ol>
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) {
    return <span className="text-muted-foreground">empty</span>;
  }
  return (
    <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1">
      {entries.map(([key, item]) => (
        <div key={key} className="contents">
          <dt>
            <code dir="ltr" className="break-all font-mono text-xs text-muted-foreground">
              {key}
            </code>
          </dt>
          <dd className="min-w-0">
            <StructuredValue value={item} depth={depth + 1} />
          </dd>
        </div>
      ))}
    </dl>
  );
}
